import React from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-hot-toast'
import { BaseView, BaseText } from '@/components/base'
import { AppButton } from '@/components/AppButton'
import { Modal } from '@/components/Modal'
import { WarningEllipses } from '@/assets/icons'
import { useDeleteWithdrawCryptoAddress } from '@/domain/wallet'
import useOffsets from '@/hooks/useOffsets'

export interface DeleteAddressModalProps {
  isVisible: boolean
  onClose: () => void
  addressId: string
  addressName?: string
  onAfterDelete?: () => void
}

export const DeleteAddressModal: React.FC<DeleteAddressModalProps> = ({
  isVisible,
  onClose,
  addressId,
  addressName,
  onAfterDelete,
}) => {
  const { t } = useTranslation()
  const offsets = useOffsets()

  const { mutateAsync: deleteAddress, isPending } =
    useDeleteWithdrawCryptoAddress({
      onSuccess: () => {
        toast.success(t('translation:walletDeleted'), {
          icon: '✅',
        })
        onAfterDelete?.()
        onClose()
      },
      onError: () => {
        toast.error(t('api:errors.somethingWrong'), {
          icon: '⚠️',
        })
      },
    })

  const handleDeleteAddress = async () => {
    try {
      await deleteAddress({ address_id: addressId })
    } catch (error) {
      console.error('Error delete address: ', error)
      // Toast error уже обрабатывается в хуке через onError
    }
  }

  return (
    <Modal
      isOpen={isVisible}
      onClose={!isPending ? onClose : () => {}}
      showHandle={true}
    >
      <BaseView
        data-component="delete-address-modal"
        style={{
          paddingTop: 20,
          paddingBottom: offsets.bottom - 16,
        }}
      >
        {/* Warning Icon */}
        <BaseView center style={{ marginBottom: 32 }}>
          <WarningEllipses size={110} color="#F32C3E" secondColor="#fff" />
        </BaseView>

        {/* Title */}
        <BaseView center px={12} style={{ marginBottom: 16 }}>
          <BaseText
            fontSize={28}
            fontFamily="semibold"
            color="label.default"
            style={{
              textAlign: 'center',
              lineHeight: '34px',
            }}
          >
            {t('bottomSheets:deleteAddress.title')}
          </BaseText>
        </BaseView>

        {/* Description */}
        <BaseView center style={{ marginBottom: 48 }}>
          <BaseText
            fontSize={16}
            fontFamily="medium"
            color="label.description"
            style={{
              textAlign: 'center',
              lineHeight: '22px',
            }}
          >
            {t('bottomSheets:deleteAddress.description')}
          </BaseText>
        </BaseView>

        {/* Buttons */}
        <BaseView gap={12}>
          <BaseView row gap={12}>
            <AppButton
              type="secondaryGray"
              onPress={onClose}
              disabled={isPending}
              label={t('form:actions.cancel')}
              isSharedSpace
            />

            <AppButton
              type="primaryGradient"
              onPress={handleDeleteAddress}
              disabled={isPending}
              isLoading={isPending}
              label={t('form:actions.delete')}
              isSharedSpace
            />
          </BaseView>
        </BaseView>
      </BaseView>
    </Modal>
  )
}
